import { GuideTestRunner } from '@/cowork/guide/GuideTestRunner';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from './PageHeader';
import {
  X,
  AlertTriangle,
  Trash2,
  CheckCircle2,
  Rocket,
  Upload,
  Info,
  Sparkles,
  LogIn,
  UserPlus,
} from 'lucide-react';

/* ─── Brand logo imports (color variants) ─── */
import logoTeal from '../imports/8_(2).svg';       // #00b1a5 — success
import logoPurple from '../imports/3_(1).svg';     // #be43ff — info
import logoWhite from '../imports/4_(1).svg';      // #fff — upgrade (on gradient)
import logoDarkPurple from '../imports/1_(3).svg'; // #2e024c — upload

/* ─── Modal Wrapper ─── */
interface ModalProps {
  open: boolean;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}
const Modal: React.FC<ModalProps> = ({ open, onClose, size = 'md', children }) => {
  if (!open) return null;
  const maxW = size === 'sm' ? 'max-w-[400px]' : size === 'lg' ? 'max-w-[600px]' : 'max-w-[480px]';
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`relative bg-white rounded-[24px] w-full ${maxW} overflow-hidden`}
        style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)' }}
      >
        {children}
      </div>
    </div>
  );
};

/* ─── Modal Close Button ─── */
const ModalClose: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute top-4 right-4 z-10 w-8 h-8 rounded-lg flex items-center justify-center text-[#98a2b3] hover:text-[#344054] hover:bg-gray-100 transition-all"
  >
    <X className="w-4 h-4" />
  </button>
);

/* ─── CTA Card ─── */
interface CTACardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  buttonLabel: string;
  buttonVariant?: 'primary' | 'destructive' | 'secondary';
  onClick: () => void;
}
const CTACard: React.FC<CTACardProps> = ({ icon, title, description, buttonLabel, buttonVariant = 'primary', onClick }) => {
  const btnClasses =
    buttonVariant === 'destructive'
      ? 'bg-red-500 hover:bg-red-600 text-white'
      : buttonVariant === 'secondary'
      ? 'bg-gray-100 hover:bg-gray-200 text-[#344054]'
      : 'bg-[#be43ff] hover:bg-[#a830e0] text-white';
  return (
    <div
      className="bg-white rounded-[24px] p-6 flex flex-col gap-4 group cursor-pointer hover:shadow-lg transition-all duration-200"
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)' }}
      onClick={onClick}
    >
      <div className="w-11 h-11 rounded-xl bg-[#f8f9fa] flex items-center justify-center">{icon}</div>
      <div>
        <h3 className="text-[#101828] text-sm mb-1" style={{ fontWeight: 600 }}>{title}</h3>
        <p className="text-[#667085] text-xs leading-relaxed">{description}</p>
      </div>
      <button
        className={`mt-auto px-4 py-2 rounded-xl text-xs transition-all active:scale-[0.97] ${btnClasses}`}
        style={{ fontWeight: 600 }}
      >
        {buttonLabel}
      </button>
    </div>
  );
};

/* ════════════════════════════════════════════
   DevModePage
   ════════════════════════════════════════════ */
export const DevModePage: React.FC = () => {
  const navigate = useNavigate();
  const [confirmModal, setConfirmModal] = useState(false);
  const [destructiveModal, setDestructiveModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [infoModal, setInfoModal] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);

  return (
    <div className="p-2.5 min-h-full">
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm min-h-[calc(100vh-4.5rem)] p-8">
        <PageHeader
          title="Dev Mode"
          subtitle="Test modals, dialogs, and UI components in isolation."
        />

        <GuideTestRunner />

        {/* CTA Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-8">
          <CTACard
            icon={<CheckCircle2 className="w-5 h-5 text-[#667085]" />}
            title="Confirmation Modal"
            description="Standard confirmation dialog with primary action."
            buttonLabel="Preview"
            buttonVariant="primary"
            onClick={() => setConfirmModal(true)}
          />
          <CTACard
            icon={<Trash2 className="w-5 h-5 text-red-500" />}
            title="Destructive Modal"
            description="Dangerous action warning with destructive CTA."
            buttonLabel="Preview"
            buttonVariant="destructive"
            onClick={() => setDestructiveModal(true)}
          />
          <CTACard
            icon={<Sparkles className="w-5 h-5 text-[#00b1a5]" />}
            title="Success Modal"
            description="Celebration state with brand teal accents."
            buttonLabel="Preview"
            buttonVariant="secondary"
            onClick={() => setSuccessModal(true)}
          />
          <CTACard
            icon={<Info className="w-5 h-5 text-[#be43ff]" />}
            title="Info Modal"
            description="Informational dialog with tips and guidance."
            buttonLabel="Preview"
            buttonVariant="primary"
            onClick={() => setInfoModal(true)}
          />
          <CTACard
            icon={<Rocket className="w-5 h-5 text-[#be43ff]" />}
            title="Upgrade Modal"
            description="Premium upsell with gradient hero and feature list."
            buttonLabel="Preview"
            buttonVariant="primary"
            onClick={() => setUpgradeModal(true)}
          />
          <CTACard
            icon={<Upload className="w-5 h-5 text-[#667085]" />}
            title="Upload Modal"
            description="File upload with drag-and-drop zone and progress."
            buttonLabel="Preview"
            buttonVariant="secondary"
            onClick={() => setUploadModal(true)}
          />
          <CTACard
            icon={<LogIn className="w-5 h-5 text-[#be43ff]" />}
            title="Login Page"
            description="Full-screen login experience with brand gradient background."
            buttonLabel="Preview"
            buttonVariant="primary"
            onClick={() => navigate('/login')}
          />
          <CTACard
            icon={<UserPlus className="w-5 h-5 text-[#be43ff]" />}
            title="Signup Page"
            description="Full-screen signup experience with brand gradient background."
            buttonLabel="Preview"
            buttonVariant="primary"
            onClick={() => navigate('/signup')}
          />
        </div>
      </div>

      {/* ─── Confirmation Modal ─── */}
      <Modal open={confirmModal} onClose={() => setConfirmModal(false)} size="sm">
        <ModalClose onClick={() => setConfirmModal(false)} />
        <div className="p-8 pt-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#be43ff]/10 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-7 h-7 text-[#be43ff]" />
          </div>
          <h3 className="text-[#101828] text-lg mb-2" style={{ fontWeight: 600 }}>
            Confirm Action
          </h3>
          <p className="text-[#667085] text-sm mb-6 max-w-[320px] mx-auto">
            Are you sure you want to proceed? This action will apply changes to your campaign.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => setConfirmModal(false)}
              className="px-6 py-2.5 rounded-xl border border-gray-200 text-sm text-[#344054] hover:bg-gray-50 transition-all active:scale-[0.97]"
              style={{ fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              onClick={() => setConfirmModal(false)}
              className="px-6 py-2.5 rounded-xl bg-[#be43ff] text-white text-sm hover:bg-[#a830e0] transition-all active:scale-[0.97]"
              style={{ fontWeight: 600 }}
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      {/* ─── Destructive Modal ─── */}
      <Modal open={destructiveModal} onClose={() => setDestructiveModal(false)} size="sm">
        <ModalClose onClick={() => setDestructiveModal(false)} />
        <div className="p-8 pt-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="w-7 h-7 text-red-500" />
          </div>
          <h3 className="text-[#101828] text-lg mb-2" style={{ fontWeight: 600 }}>
            Delete Campaign?
          </h3>
          <p className="text-[#667085] text-sm mb-6 max-w-[320px] mx-auto">
            This will permanently remove the campaign and all associated data. This cannot be undone.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => setDestructiveModal(false)}
              className="px-6 py-2.5 rounded-xl border border-gray-200 text-sm text-[#344054] hover:bg-gray-50 transition-all active:scale-[0.97]"
              style={{ fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              onClick={() => setDestructiveModal(false)}
              className="px-6 py-2.5 rounded-xl bg-red-500 text-white text-sm hover:bg-red-600 transition-all active:scale-[0.97]"
              style={{ fontWeight: 600 }}
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* ─── Success Modal ─── */}
      <Modal open={successModal} onClose={() => setSuccessModal(false)} size="sm">
        <ModalClose onClick={() => setSuccessModal(false)} />
        <div className="p-8 pt-8 text-center">
          {/* Icon first */}
          <div className="w-14 h-14 rounded-2xl bg-[#00b1a5]/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7 text-[#00b1a5]" />
          </div>
          {/* Brand logo below icon */}
          <div className="flex justify-center mb-5">
            <img src={logoTeal} alt="Beatly" className="h-8 w-auto" />
          </div>
          <h3 className="text-[#101828] text-lg mb-2" style={{ fontWeight: 600 }}>
            Campaign Published!
          </h3>
          <p className="text-[#667085] text-sm mb-4 max-w-[320px] mx-auto">
            Your campaign is now live and visible to creators in the marketplace.
          </p>
          <div className="flex flex-col items-center gap-3 mb-6">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#00b1a5]" />
              <span className="text-xs text-[#344054]">3 creators matched</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#be43ff]" />
              <span className="text-xs text-[#344054]">Auto-outreach enabled</span>
            </div>
          </div>
          <button
            onClick={() => setSuccessModal(false)}
            className="px-8 py-2.5 rounded-xl bg-[#00b1a5] text-white text-sm hover:bg-[#009e93] transition-all active:scale-[0.97]"
            style={{ fontWeight: 600 }}
          >
            View Campaign
          </button>
        </div>
      </Modal>

      {/* ─── Info Modal ─── */}
      <Modal open={infoModal} onClose={() => setInfoModal(false)} size="md">
        <ModalClose onClick={() => setInfoModal(false)} />
        <div className="p-8 relative">
          {/* Icon + brand logo centered at top */}
          <div className="flex flex-col items-center mb-5">
            <div className="w-14 h-14 rounded-2xl bg-[#be43ff]/10 flex items-center justify-center mb-4">
              <Info className="w-7 h-7 text-[#be43ff]" />
            </div>
            <img src={logoPurple} alt="Beatly" className="h-8 w-auto" />
          </div>
          <h3 className="text-[#101828] text-lg mb-2" style={{ fontWeight: 600 }}>
            Campaign Best Practices
          </h3>
          <p className="text-[#667085] text-sm mb-5">
            Follow these tips to maximize your campaign performance and creator engagement.
          </p>
          <div className="space-y-3 mb-6">
            {[
              'Set clear deliverables and timelines for creators',
              'Use branded hashtags for tracking and discovery',
              'Provide creative briefs with visual references',
              'Enable auto-outreach to save 60% on sourcing time',
            ].map((tip, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#be43ff]/10 flex items-center justify-center mt-0.5 shrink-0">
                  <span className="text-[10px] text-[#be43ff]" style={{ fontWeight: 700 }}>{i + 1}</span>
                </div>
                <span className="text-sm text-[#344054]">{tip}</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => setInfoModal(false)}
            className="w-full py-2.5 rounded-xl bg-[#be43ff] text-white text-sm hover:bg-[#a830e0] transition-all active:scale-[0.97]"
            style={{ fontWeight: 600 }}
          >
            Got It
          </button>
        </div>
      </Modal>

      {/* ─── Upgrade Modal ─── */}
      <Modal open={upgradeModal} onClose={() => setUpgradeModal(false)} size="md">
        <ModalClose onClick={() => setUpgradeModal(false)} />
        <div className="relative overflow-hidden">
          {/* Gradient hero */}
          <div
            className="relative px-8 py-10 text-center"
            style={{ background: 'linear-gradient(135deg, #be43ff 0%, #7b1fa2 50%, #2e024c 100%)' }}
          >
            {/* Brand logo watermark on gradient */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
              <img src={logoWhite} alt="" className="w-44 h-auto" />
            </div>
            {/* Decorative circles */}
            <div className="absolute top-4 right-8 w-20 h-20 rounded-full bg-white/5" />
            <div className="absolute bottom-2 left-6 w-12 h-12 rounded-full bg-white/5" />
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center mx-auto mb-4">
                <Rocket className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-white text-xl mb-2" style={{ fontWeight: 600 }}>
                Upgrade to Pro
              </h3>
              <p className="text-white/70 text-sm max-w-[300px] mx-auto">
                Unlock advanced analytics, unlimited campaigns, and priority creator matching.
              </p>
            </div>
          </div>
          {/* Feature list */}
          <div className="px-8 py-6 space-y-3">
            {[
              'Unlimited active campaigns',
              'AI-powered creator matching',
              'Advanced ROI analytics',
              'Priority support & onboarding',
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle2 className="w-4 h-4 text-[#be43ff] shrink-0" />
                <span className="text-sm text-[#344054]">{feature}</span>
              </div>
            ))}
          </div>
          <div className="px-8 pb-8 flex gap-3">
            <button
              onClick={() => setUpgradeModal(false)}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-[#344054] hover:bg-gray-50 transition-all active:scale-[0.97]"
              style={{ fontWeight: 500 }}
            >
              Maybe Later
            </button>
            <button
              onClick={() => setUpgradeModal(false)}
              className="flex-1 py-2.5 rounded-xl bg-[#be43ff] text-white text-sm hover:bg-[#a830e0] transition-all active:scale-[0.97]"
              style={{ fontWeight: 600 }}
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </Modal>

      {/* ─── Upload Modal ─── */}
      <Modal open={uploadModal} onClose={() => setUploadModal(false)} size="md">
        <ModalClose onClick={() => setUploadModal(false)} />
        <div className="p-8 relative">
          {/* Icon + brand logo centered at top */}
          <div className="flex flex-col items-center mb-5">
            <div className="w-14 h-14 rounded-2xl bg-[#2e024c]/10 flex items-center justify-center mb-4">
              <Upload className="w-7 h-7 text-[#2e024c]" />
            </div>
            <img src={logoDarkPurple} alt="Beatly" className="h-8 w-auto" />
          </div>
          <h3 className="text-[#101828] text-lg mb-1" style={{ fontWeight: 600 }}>
            Upload Assets
          </h3>
          <p className="text-[#667085] text-sm mb-5">
            Drag and drop your campaign assets or browse to select files.
          </p>
          {/* Drop zone */}
          <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center mb-5 hover:border-[#be43ff]/40 hover:bg-[#be43ff]/[0.02] transition-all cursor-pointer">
            <Upload className="w-8 h-8 text-[#98a2b3] mx-auto mb-3" />
            <p className="text-sm text-[#344054] mb-1" style={{ fontWeight: 500 }}>
              Drop files here or click to browse
            </p>
            <p className="text-xs text-[#98a2b3]">PNG, JPG, MP4, PDF up to 50MB</p>
          </div>
          {/* Sample uploaded file */}
          <div className="bg-[#f8f9fa] rounded-xl p-3 flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-[#be43ff]/10 flex items-center justify-center shrink-0">
              <span className="text-[10px] text-[#be43ff]" style={{ fontWeight: 700 }}>PNG</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-[#344054] truncate" style={{ fontWeight: 500 }}>campaign-banner-v2.png</p>
              <p className="text-[10px] text-[#98a2b3]">2.4 MB</p>
            </div>
            <div className="text-[10px] text-[#00b1a5]" style={{ fontWeight: 600 }}>Done</div>
          </div>
          <button
            onClick={() => setUploadModal(false)}
            className="w-full py-2.5 rounded-xl bg-[#2e024c] text-white text-sm hover:bg-[#3d0a5c] transition-all active:scale-[0.97]"
            style={{ fontWeight: 600 }}
          >
            Upload Files
          </button>
        </div>
      </Modal>

      {/* ─── Login Page Preview ─── */}
      {/* ─── Signup Page Preview ─── */}
    </div>
  );
};
